import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { useMember } from '@/lib/use-session';
import { resourcesKeys, type ResourceDetailView } from '@/lib/queries/resources';
import { getApiClient } from '@/lib/api-client';
import { isNotFoundApiError, throwApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import {
  ResourceArticleView,
  type RelatedResource,
} from './-components/ResourceArticleView';

async function fetchResourceDetail(id: string): Promise<ResourceDetailView> {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/resources/{id}', {
    params: { path: { id } },
  });
  if (data === undefined) throwApiError(error, response);
  return data.data as ResourceDetailView;
}

async function fetchRelatedResources(
  excludeId: string,
  tags: ReadonlyArray<{ slug: string; label: string }>,
  kind?: string,
): Promise<readonly RelatedResource[]> {
  try {
    const client = getApiClient();
    const tagSlug = tags[0]?.slug;
    const isAllowedKind =
      kind === 'article' ||
      kind === 'video' ||
      kind === 'toolkit' ||
      kind === 'workshop' ||
      kind === 'essay';
    const { data } = await client.GET('/api/v1/resources', {
      params: {
        query: {
          pageSize: 6,
          ...(tagSlug !== undefined ? { tags: tagSlug } : {}),
          ...(isAllowedKind
            ? {
                contentType: kind as
                  | 'article'
                  | 'video'
                  | 'toolkit'
                  | 'workshop'
                  | 'essay',
              }
            : {}),
        },
      },
    });
    if (data === undefined) return [];
    return data.items
      .filter((r) => r.id !== excludeId)
      .slice(0, 3)
      .map((r) => ({
        id: r.id,
        title: r.title,
        kind: r.kind,
      }));
  } catch {
    return [];
  }
}

export const Route = createFileRoute('/_authed/resources/$id/')({
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  component: ResourceDetailPage,
});

function ResourceDetailPage() {
  const { id } = Route.useParams();
  const me = useMember();

  const resourceQuery = useQuery({
    queryKey: resourcesKeys.detail(id),
    queryFn: () => fetchResourceDetail(id),
    retry: false,
  });
  const resource = resourceQuery.data ?? null;
  const relatedQuery = useQuery({
    queryKey: ['resources', 'related', id] as const,
    queryFn: () =>
      fetchRelatedResources(resource!.id, resource!.tags, resource!.kind),
    enabled: resource !== null,
  });

  // 404 → branded NotFoundPage directly (render-context `throw notFound()`
  // lands in TanStack's catch-boundary, not its 404 surface).
  if (isNotFoundApiError(resourceQuery.error)) return <NotFoundPage />;
  if (resourceQuery.isError) throw notFound();
  if (me === null) return null;

  const viewerIsAdmin = me.platformRole === 'admin';
  const viewerCanEdit = viewerIsAdmin;

  return (
    <ResourceArticleView
      resourceId={id}
      resource={resource}
      related={relatedQuery.data ?? null}
      viewerIsAdmin={viewerIsAdmin}
      viewerCanEdit={viewerCanEdit}
    />
  );
}
